import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Calendar from './Calendar';

const today = new Date();
const addDays = (date, days) => {
  const d = new Date(date);
  d.setDate(d.getDate() + days);
  return d;
};
function formatInputDate(date) {
  if (!date) return '';
  return date.toISOString().slice(0, 10);
}
function parseInputDate(str) {
  if (!str) return null;
  const d = new Date(str);
  return isNaN(d) ? null : d;
}

const CalendarDemo = () => {
  const [multiple, setMultiple] = useState(false);
  const [minDate, setMinDate] = useState(null);
  const [maxDate, setMaxDate] = useState(null);
  const [selected, setSelected] = useState(multiple ? [] : null);

  // Reset selection when multiple mode changes
  React.useEffect(() => {
    setSelected(multiple ? [] : null);
  }, [multiple]);

  // For demo: quick set min/max
  const quickSetMin = () => setMinDate(addDays(today, -3));
  const quickSetMax = () => setMaxDate(addDays(today, 7));
  const clearMin = () => setMinDate(null);
  const clearMax = () => setMaxDate(null);

  // Simple selection handler
  const handleSelect = (dateOrArr) => {
    setSelected(dateOrArr);
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: `
        linear-gradient(135deg, #667eea 0%, #764ba2 100%),
        radial-gradient(circle at 20% 80%, #f093fb 0%, transparent 50%),
        radial-gradient(circle at 80% 20%, #f5576c 0%, transparent 50%),
        radial-gradient(circle at 40% 40%, #4facfe 0%, transparent 50%)
      `,
      backgroundBlendMode: 'overlay, screen, screen, screen',
      padding: '40px 20px',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '40px',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <motion.div
        initial={{ opacity: 0, y: -40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        style={{ textAlign: 'center', color: 'white', marginBottom: 20 }}
      >
        <h1 style={{
          fontSize: '2.5rem',
          fontWeight: 'bold',
          margin: '0 0 10px 0',
          textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
          background: 'linear-gradient(45deg, #fff, #f0f0f0)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          backgroundClip: 'text'
        }}>
          Liquid Glass Calendar
        </h1>
        <p style={{
          fontSize: '1.2rem',
          opacity: 0.9,
          margin: '0',
          textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)'
        }}>
          Beautiful, animated glass morphism calendar month view
        </p>
      </motion.div>

      {/* Options */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        style={{
          display: 'flex',
          gap: 24,
          alignItems: 'center',
          background: 'rgba(255,255,255,0.08)',
          borderRadius: 16,
          padding: '16px 32px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
          marginBottom: 16
        }}
      >
        <label style={{ color: 'white', fontWeight: 500, fontSize: '1.1rem' }}>
          <input
            type="checkbox"
            checked={multiple}
            onChange={e => setMultiple(e.target.checked)}
            style={{ marginRight: 8 }}
          />
          Consecutive day selection
        </label>
        <label style={{ color: 'white', fontWeight: 500, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          Min date:
          <input
            type="date"
            value={formatInputDate(minDate)}
            onChange={e => setMinDate(parseInputDate(e.target.value))}
            style={{ borderRadius: 6, border: 'none', padding: '2px 8px', fontSize: '1rem' }}
          />
          <button onClick={clearMin} style={{ marginLeft: 4, border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 4, cursor: 'pointer', padding: '2px 8px' }}>Clear</button>
        </label>
        <label style={{ color: 'white', fontWeight: 500, fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 6 }}>
          Max date:
          <input
            type="date"
            value={formatInputDate(maxDate)}
            onChange={e => setMaxDate(parseInputDate(e.target.value))}
            style={{ borderRadius: 6, border: 'none', padding: '2px 8px', fontSize: '1rem' }}
          />
          <button onClick={clearMax} style={{ marginLeft: 4, border: 'none', background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: 4, cursor: 'pointer', padding: '2px 8px' }}>Clear</button>
        </label>
      </motion.div>

      {/* Instructions */}
      {multiple && (
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          style={{
            background: 'rgba(59,130,246,0.15)',
            borderRadius: 12,
            padding: '12px 20px',
            border: '1px solid rgba(59,130,246,0.3)',
            color: '#aee9ff',
            fontSize: '0.95rem',
            textAlign: 'center',
            maxWidth: 400
          }}
        >
          💡 <strong>How to select consecutive days:</strong><br />
          Click the first date, then click the last date to select all days in between.
        </motion.div>
      )}

      {/* Calendar */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.2 }}
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 32 }}
      >
        <Calendar
          multiple={multiple}
          minDate={minDate}
          maxDate={maxDate}
          selected={selected}
          onSelect={handleSelect}
        />
        <div style={{ color: 'white', fontSize: '1.1rem', marginTop: 16, textAlign: 'center' }}>
          {multiple ? (
            <>
              <span style={{ fontWeight: 600 }}>Selected range:</span>{' '}
              {Array.isArray(selected) && selected.length > 0
                ? `${selected[0].toLocaleDateString()} - ${selected[selected.length - 1].toLocaleDateString()} (${selected.length} days)`
                : <span style={{ opacity: 0.7 }}>None</span>}
            </>
          ) : (
            selected ? (
              <>
                <span style={{ fontWeight: 600 }}>Selected date:</span> {selected.toLocaleDateString()}
              </>
            ) : (
              <span style={{ opacity: 0.7 }}>No date selected</span>
            )
          )}
        </div>
        <div style={{ color: '#aee9ff', fontSize: '1rem', marginTop: 8, textAlign: 'center', fontStyle: 'italic' }}>
          <span>Today is {today.toLocaleDateString()} (highlighted in the calendar)</span>
        </div>
      </motion.div>

      {/* Features List */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.4 }}
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '20px',
          width: '100%',
          maxWidth: '1200px',
          marginTop: 40
        }}
      >
        <h2 style={{ color: 'white', fontSize: '2rem', margin: '0', textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)' }}>
          Calendar Features
        </h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '20px',
          width: '100%'
        }}>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Glass Effects</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Backdrop-filter: blur(24px)</li>
              <li>Multi-layered gradients</li>
              <li>Glass borders & shadows</li>
              <li>Soft highlights</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Navigation</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Month/year header</li>
              <li>Prev/next month buttons</li>
              <li>Animated transitions</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Highlighting</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Today highlight (see note above)</li>
              <li>Selected day highlight</li>
              <li>Disabled days for prev/next month and min/max</li>
              <li>Range selection for consecutive days</li>
            </ul>
          </div>
          <div style={{
            background: 'rgba(255, 255, 255, 0.1)',
            backdropFilter: 'blur(10px)',
            borderRadius: '12px',
            padding: '20px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ color: 'white', margin: '0 0 10px 0' }}>Responsive</h3>
            <ul style={{ color: 'white', margin: 0, paddingLeft: '20px' }}>
              <li>Responsive layout</li>
              <li>Touch-friendly buttons</li>
              <li>Modern, accessible design</li>
            </ul>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default CalendarDemo; 